import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreatePayrollDTO,
  ListPayrollQuery,
  UpdatePayrollDTO,
} from "@/modules/hr/payroll/domain/types";
import { payrollApi } from "./client";

const keys = {
  list: (q: ListPayrollQuery) => ["hr", "payroll", "list", q] as const,
  summary: (q: Omit<ListPayrollQuery, "offset" | "limit" | "sort">) =>
    ["hr", "payroll", "summary", q] as const,
  listByPeriod: (
    year: number,
    month: number,
    opts?: {
      offset?: number;
      limit?: number;
      departmentId?: string;
      status?: string;
      search?: string;
      sort?: ListPayrollQuery["sort"];
    },
  ) => ["hr", "payroll", "period", year, month, opts] as const,
  detail: (id: string) => ["hr", "payroll", "detail", id] as const,
  slip: (id: string) => ["hr", "payroll", "slip", id] as const,
};

export function usePayrollListQuery(query: ListPayrollQuery) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => payrollApi.list(query),
  });
}

export function usePayrollSummaryQuery(
  query: Omit<ListPayrollQuery, "offset" | "limit" | "sort">,
) {
  return useQuery({
    queryKey: keys.summary(query),
    queryFn: () => payrollApi.getSummary(query),
  });
}

export function usePayrollByPeriodQuery(
  year: number,
  month: number,
  opts?: {
    offset?: number;
    limit?: number;
    departmentId?: string;
    status?: "draft" | "confirmed" | "paid";
    search?: string;
    sort?: ListPayrollQuery["sort"];
  },
) {
  return useQuery({
    queryKey: keys.listByPeriod(year, month, opts),
    queryFn: () => payrollApi.listByPeriod(year, month, opts),
    enabled: Number.isInteger(year) && Number.isInteger(month),
  });
}

export function usePayrollDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.detail(id ?? ""),
    queryFn: () => payrollApi.getById(id!),
    enabled: Boolean(id),
  });
}

export function usePayrollSlipQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.slip(id ?? ""),
    queryFn: () => payrollApi.getSlip(id!),
    enabled: Boolean(id),
  });
}

export function usePayrollCreateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePayrollDTO) => payrollApi.create(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "payroll"] });
    },
  });
}

export function usePayrollUpdateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePayrollDTO }) =>
      payrollApi.update(id, dto),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["hr", "payroll"] });
    },
  });
}

export function usePayrollStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "confirmed" | "paid" | "draft" }) =>
      payrollApi.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["hr", "payroll"] });
    },
  });
}

export function usePayrollDeleteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => payrollApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "payroll"] });
    },
  });
}
